/** Motion metric field resolution and display helpers for activity UI. */

export const MOTION_TOOLTIPS = {
  avgSpeed:
    "Distance divided by detected moving time. Stops are excluded.",
  movingTime:
    "Time detected as actively moving, based on the device timer, speed stream, or distance stream.",
  elapsedTime:
    "Total time from activity start to finish, including stops and breaks.",
  avgCadence:
    "Average cadence while pedalling. Zero cadence, coasting, and stopped time are excluded.",
  overallCadence:
    "Average cadence during moving time, including zero cadence while coasting.",
  legacyCalculation: "Legacy calculation",
};

const SOURCE_LABELS = {
  device_timer: "Device timer",
  speed_stream: "Speed data",
  distance_stream: "Distance data",
  elapsed_fallback: "Elapsed-time fallback",
};

const CONFIDENCE_LABELS = {
  high: "High",
  medium: "Medium",
  limited: "Limited",
  low: "Limited",
};

export function resolveMovingSpeedMs(activity) {
  if (!activity) return null;
  if (isFiniteNumber(activity.average_moving_speed_ms)) return activity.average_moving_speed_ms;
  if (isFiniteNumber(activity.avg_speed_ms)) return activity.avg_speed_ms;
  return null;
}

export function resolvePedallingCadence(activity) {
  if (!activity) return null;
  if (isFiniteNumber(activity.average_cadence_pedalling)) return activity.average_cadence_pedalling;
  if (isFiniteNumber(activity.avg_cadence)) return activity.avg_cadence;
  return null;
}

export function resolveOverallCadence(activity) {
  if (!activity) return null;
  if (isFiniteNumber(activity.average_cadence_overall)) return activity.average_cadence_overall;
  return null;
}

export function resolveMovingTimeSeconds(activity) {
  if (!activity) return null;
  if (isFiniteNumber(activity.moving_time_seconds)) return activity.moving_time_seconds;
  return null;
}

/** Moving time for cross-activity totals; legacy activities fall back to duration_seconds. */
export function resolveMovingTimeSecondsForAggregation(activity) {
  if (!activity) return 0;
  if (isFiniteNumber(activity.moving_time_seconds)) return activity.moving_time_seconds;
  if (isFiniteNumber(activity.duration_seconds)) return activity.duration_seconds;
  return 0;
}

export function resolveElapsedSeconds(activity) {
  if (!activity) return null;
  if (isFiniteNumber(activity.elapsed_time_seconds)) return activity.elapsed_time_seconds;
  if (isFiniteNumber(activity.duration_seconds)) return activity.duration_seconds;
  return null;
}

export function resolveMaxCadence(activity) {
  if (!activity) return null;
  if (isFiniteNumber(activity.max_cadence)) return activity.max_cadence;
  return null;
}

export function formatCadenceRpm(value) {
  if (!isFiniteNumber(value)) return null;
  return Math.round(value);
}

export function getMotionSourceLabel(source) {
  if (!source) return null;
  return SOURCE_LABELS[source] || null;
}

export function getMotionConfidenceLabel(confidence) {
  if (!confidence) return null;
  return CONFIDENCE_LABELS[String(confidence).toLowerCase()] || null;
}

export function hasMotionSourceMetadata(activity) {
  return Boolean(activity?.moving_time_source);
}

export function isLegacyMotionSummary(activity) {
  if (!activity) return false;
  if (activity.summary_version === "motion_v1") return false;
  if (activity.moving_time_source) return false;
  return true;
}

export function formatMotionSourceSummary(activity) {
  if (!hasMotionSourceMetadata(activity)) return null;
  const source = getMotionSourceLabel(activity.moving_time_source);
  const confidence = getMotionConfidenceLabel(activity.moving_time_confidence);
  if (!source) return null;
  return confidence ? `${source} · ${confidence} confidence` : source;
}

/** Shared resolver for list/card speed when cards expose average speed. */
export function resolveActivityCardSpeedMs(activity) {
  return resolveMovingSpeedMs(activity);
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}
